/**
 * 「在途」横排(工作台② / `/works` 顶部同款语义)。
 *
 * ★为什么在途要独立成节、不受任何筛选影响:此前在途任务混在主列表里靠一个「生成中」chip 筛,
 * 你筛了「成功」再建一条新任务,那条任务直接从视野里消失。在途是**当下最该看见**的东西。
 *
 * 有在途才 5s 轮询,没有就不轮询(不给后端白打请求);全空整节不渲染。
 */
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { MediaInProgressCard } from "@/entities/media-asset/ui/MediaInProgressCard";
import { taskStatusLabel } from "@/shared/lib/taskStatus";
import { cn } from "@/shared/lib/utils";
import { isLineWired, listRunningWorks } from "../api/recentWorksApi";
import { useAuth } from "@/contexts/AuthContext";
import { workCreatorName, workOriginLabel } from "../model/cardLabels";
import { mergeWorks } from "../model/mappers";
import { useRefreshOnDrain } from "@/shared/hooks/useRefreshOnDrain";
import type { WorkItem, WorkLineKey } from "../model/types";

export interface InFlightRowProps {
  lines: WorkLineKey[];
  onOpen?: (item: WorkItem) => void;
  className?: string;
}

export function InFlightRow({ lines, onOpen, className }: InFlightRowProps) {
  const { user } = useAuth();
  const wired = lines.filter(isLineWired);
  const qs = useQueries({
    queries: wired.map((l) => ({
      queryKey: ["running-works", l],
      queryFn: () => listRunningWorks(l),
      retry: 1,
      // 可选带:拉失败整节不渲染,不弹全局破坏性 toast(codex review P2 同类)。
      meta: { suppressGlobalErrorToast: true },
      refetchInterval: (q: { state: { data?: WorkItem[] } }) =>
        (q.state.data ?? []).length > 0 ? 5000 : false,
    })),
  });

  // 在途按提交时间倒序(最新提交的在最前),上限 6 条:再多说明该去 /works 看全量
  const items = mergeWorks(
    qs.map((q) => q.data ?? []),
    6
  );

  // ★在途清空时刷新「最近作品」(codex review P2):否则完成的任务从「进行中」消失后,
  //   RecentWorksRow 还 fresh 着,任务要等下次 refetch 才出现,中间两个列表都看不到它。
  const qc = useQueryClient();
  const inFlightTotal = qs.reduce((n, q) => n + (q.data?.length ?? 0), 0);
  useRefreshOnDrain(inFlightTotal, () => qc.invalidateQueries({ queryKey: ["recent-works"] }));

  if (items.length === 0) return null;

  return (
    <section data-testid="in-flight-row" className={cn("work-row", className)}>
      {/* ★计数用**真实总数**而不是截断后的条数(测试反馈 E-09a,爱民 8·10):
          首页显示「进行中 (6)」、同一时刻作品页显示「进行中 (20)」。
          只铺 6 条是有意的(再多该去 /works 看全量),但把括号里的数字也写成 6
          等于告诉用户"一共就这 6 个" —— 而 `inFlightTotal` 就在上面一行算好了,
          只是没拿来用。超出部分给一个能点的出口,别让用户以为剩下的丢了。 */}
      <div className="mb-3 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-warning" />
        <h2 className="text-sm font-semibold">进行中</h2>
        <span className="text-xs text-muted-foreground">({inFlightTotal})</span>
        {inFlightTotal > items.length && (
          <Link
            to="/works"
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            这里只显示 {items.length} 个 · 查看全部 →
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((w) => (
          <MediaInProgressCard
            key={w.uid}
            name={w.title}
            statusLabel={taskStatusLabel(w.status)}
            createdAt={w.sortAt}
            creatorName={workCreatorName(w, user?.name)}
            originLabel={workOriginLabel(w)}
            onClick={() => onOpen?.(w)}
          />
        ))}
      </div>
    </section>
  );
}
